import type {
  AtLeastOne,
  ISO8601DateTimeString,
  Path,
  ProjectId,
  UserId,
  UUIDv4,
} from "../types";
import type { CustomField } from "./customFields";
import type { StatusSet, StatusSetId } from "./statusSets"; // bidirectional cycle — type-only, safe

type CategoryPath = Path<
  Category["id"],
  Category["name"],
  { internalId: Category["internalId"] }
>;

export type CategoryType = "asset" | "system";

export type ComputedTreeFields = {
  isRoot?: boolean;
  isLeaf?: boolean;
  subcategoryIds?: Category["id"][];
  path?: CategoryPath;
};

// Superset: app fields (customFieldIds, statusSetId) + shared-web's named ComputedTreeFields
// Source: apps/acs-assets/src/models/categories.ts:20-48, libs/.../categories.ts:9-28
export type Category = {
  id: UUIDv4;
  internalId: string;
  parentId: Category["id"];
  parentInternalId: Category["internalId"];
  name: string;
  description?: string;
  isActive: boolean;
  version: number;
  categoryType: CategoryType;
  projectId: UUIDv4;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  customFieldIds?: CustomField["id"][]; // not from BE — added in store
  statusSetId?: StatusSet["id"]; // bidirectional cycle (type-only, safe)
  computedTreeFields?: ComputedTreeFields;
};

export type CategoryDTO = Omit<
  Category,
  "createdAt" | "updatedAt" | "deletedAt" | "customFieldIds" | "statusSetId"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
};

export type NewCategory = Pick<Category, "name" | "parentId" | "description">;

export type CategoryUpdate = AtLeastOne<
  Pick<Category, Exclude<CategoryEditableAttributeKey, "isActive">>
>;

export type CategoryAttribute = keyof Category;

export type CategoryVisibleAttribute = Extract<
  CategoryAttribute,
  "name" | "isActive" | "customAttributes" | "description" | "statusSetId"
>;

export type CategoryEditableAttributeKey = Extract<
  CategoryAttribute,
  "name" | "description" | "isActive"
>;

export type CategoryCustomField = CustomField & {
  inheritedFromCategoryId: CustomField["id"] | null;
};

export type CategoryCustomFieldAssignment = {
  createdBy: UserId;
  createdAt: Date;
  updatedBy: UserId;
  updatedAt: Date;
  deletedAt?: Date;
  deletedBy?: UserId;
  id: UUIDv4;
  isActive: boolean;
  version: number;
  projectId: ProjectId;
  categoryId: Category["internalId"];
  customAttributeId: CustomField["id"];
};

export type CategoryCustomFieldAssignmentDTO = Omit<
  CategoryCustomFieldAssignment,
  "createdAt" | "updatedAt" | "deletedAt"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
};

// All CategoryStatusSet* types canonically live here (app's layout — more cohesive)
// Source: apps/acs-assets/src/models/categories.ts:105-138 (merged with shared-web's statusSets.ts:145-160)

export type CategoryStatusSet = {
  categoryId: Category["internalId"]; // internalId (shared-web's consistent definition)
  statusStepSetId: StatusSetId;
};

export type CategoryStatusSetAssignment = {
  id: UUIDv4;
  version: number;
  projectId: ProjectId;
  categoryId: Category["internalId"];
  statusStepSetId: StatusSetId;
  createdBy: UserId;
  createdAt: Date;
  updatedBy: UserId;
  updatedAt: Date;
};

export type CategoryStatusSetAssignmentDTO = Omit<
  CategoryStatusSetAssignment,
  "createdAt" | "updatedAt"
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
};

export type CategoryStatusSetDTO = {
  categoryId: Category["internalId"];
  inheritedFromCategoryId?: Category["internalId"];
  statusStepSetId: StatusSetId;
};

export type CategoryStatusSetV3DTO = CategoryStatusSetDTO & {
  categoryType: CategoryType;
};

export type CategoryCustomFieldOrderDTO = {
  categoryId: Category["internalId"];
  customAttributeIds: CustomField["id"][];
};

export type CategoryCustomFieldOrder = {
  categoryId: Category["id"];
  customAttributeIds: CustomField["id"][];
};

export type CategoryTreeNode = {
  id: Category["id"];
  internalId?: Category["internalId"]; // superset: app has it (apps/.../categories.ts:152), shared-web doesn't
  depth: number | undefined;
  children?: CategoryTreeNode[];
};

// Superset of both app and shared-web ListCategoriesFilters
export type ListCategoriesFilters = {
  isActive?: boolean; // app only
  updatedAt?: ISO8601DateTimeString; // shared-web only
  maxDepth?: number;
  parentId?: Category["id"];
  id?: Category["id"][]; // shared-web only
  internalId?: Category["internalId"][];
  categoryType?: CategoryType; // app only
};

// Shared-web extra (libs/.../categories.ts:49-56)
export type ListCategoriesQueries = {
  isDeleted?: boolean;
  limit?: number;
  cursorState?: string;
  includedComputedTreeFields?: (keyof ComputedTreeFields)[];
  includeDeleted?: boolean;
  categoryType?: CategoryType;
};

export type InheritedTemplate = {
  categoryId: Category["id"];
  categoryName: Category["name"];
  isCategoryRoot?: boolean;
  id: UUIDv4;
  name: string;
};

// Used by parseCategory signature; re-exported so shared-web thin re-export can expose it
export type StatusSetAssignment = {
  categoryId: Category["internalId"];
  statusStepSetId: UUIDv4;
};

// Storage is reference-only — no name/description is ever returned; render via
// acc-classifications' `treeInfo` remote by `classificationTreeId`, not from this type.
export type CategoryClassificationStructureAssignment = {
  categoryId: Category["internalId"];
  classificationTreeId: UUIDv4;
  inherited: boolean;
  sourceCategoryId: Category["internalId"] | null;
};

// Canonical parseCategory: shared-web signature (statusSetAssignment) + app default (computedTreeFields)
// Source: apps/.../categories.ts:173-187 merged with libs/.../categories.ts:85-101
export const parseCategory = ({
  statusSetAssignment,
  name,
  createdAt,
  updatedAt,
  deletedAt,
  computedTreeFields = { subcategoryIds: [] },
  ...category
}: CategoryDTO & { statusSetAssignment?: StatusSetAssignment }): Category => ({
  ...category,
  name: name.trim(),
  computedTreeFields,
  createdAt: new Date(createdAt),
  updatedAt: new Date(updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
  ...(statusSetAssignment && {
    statusSetId: statusSetAssignment.statusStepSetId,
  }),
});

export const parseCategoryList = (categories: CategoryDTO[]): Category[] =>
  categories.map(parseCategory);

// Shared-web extra (libs/.../categories.ts:103-109)
export const parseCategoryMap = (
  categories: Record<Category["id"], CategoryDTO>
): Record<Category["id"], Category> =>
  Object.keys(categories).reduce(
    (acc, key) => ({
      ...acc,
      [key]: parseCategory(categories[key]),
    }),
    {}
  );
